import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authEnabled, getIdToken, signOut, userFromToken, type AuthUser } from './auth';

/** モックモード（Cognito 未設定）ではローカル固定ユーザーとして振る舞う。 */
const LOCAL_USER: AuthUser = {
  sub: 'local',
  email: 'local@example.com',
  name: 'you',
  // ローカルで管理画面を確認できるように、モックモードでは管理者扱いにする。
  isAdmin: true,
  roles: ['admin', 'user'],
};

interface AuthState {
  /** ログイン中のユーザー。未ログインなら null。 */
  user: AuthUser | null;
  /** 初期化（トークン復元）が完了したか。 */
  ready: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  user: null,
  ready: false,
  login: () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (!authEnabled) {
        setUser(LOCAL_USER);
        setReady(true);
        return;
      }
      try {
        const token = await getIdToken();
        if (token) setUser(userFromToken(token));
      } catch (e) {
        console.error('ログイン処理に失敗しました', e);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const value: AuthState = {
    user,
    ready,
    login: () => {
      if (authEnabled) navigate('/login');
    },
    logout: () => {
      if (authEnabled) {
        signOut();
        setUser(null);
        navigate('/login');
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
